import { useState } from 'react';
import { useAppContext } from '@/hooks/useAppContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';

export default function Settings() {
  const { config, updateConfig } = useAppContext();
  const { toast } = useToast();
  const [scryfallApiUrl, setScryfallApiUrl] = useState(config.scryfallApiUrl);
  const [tcgPlayerAffiliateUrl, setTcgPlayerAffiliateUrl] = useState(config.tcgPlayerAffiliateUrl);

  const handleScryfallApiUrlSave = () => {
    // Validate URL format
    try {
      new URL(scryfallApiUrl);
    } catch {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL for the Scryfall API.",
        variant: "destructive",
      });
      return;
    }

    // Remove trailing slash if present
    const normalizedUrl = scryfallApiUrl.replace(/\/$/, '');

    updateConfig((current) => ({
      ...current,
      scryfallApiUrl: normalizedUrl,
    }));

    setScryfallApiUrl(normalizedUrl);

    toast({
      title: "Settings saved",
      description: "Scryfall API URL has been updated.",
    });
  };

  const handleReset = () => {
    const defaultUrl = 'https://api.scryfall.com';
    setScryfallApiUrl(defaultUrl);
    updateConfig((current) => ({
      ...current,
      scryfallApiUrl: defaultUrl,
    }));

    toast({
      title: "Settings reset",
      description: "Scryfall API URL has been reset to default.",
    });
  };

  const handleTcgPlayerAffiliateUrlSave = () => {
    // Validate URL format
    try {
      new URL(tcgPlayerAffiliateUrl);
    } catch {
      toast({
        title: "Invalid URL",
        description: "Please enter a valid URL for the TCGPlayer affiliate link.",
        variant: "destructive",
      });
      return;
    }

    // Remove trailing slash if present
    const normalizedUrl = tcgPlayerAffiliateUrl.replace(/\/$/, '');

    updateConfig((current) => ({
      ...current,
      tcgPlayerAffiliateUrl: normalizedUrl,
    }));

    setTcgPlayerAffiliateUrl(normalizedUrl);

    toast({
      title: "Settings saved",
      description: "TCGPlayer affiliate URL has been updated.",
    });
  };

  const handleTcgPlayerReset = () => {
    const defaultUrl = 'https://partner.tcgplayer.com/c/6367313/1830156/21018';
    setTcgPlayerAffiliateUrl(defaultUrl);
    updateConfig((current) => ({
      ...current,
      tcgPlayerAffiliateUrl: defaultUrl,
    }));

    toast({
      title: "Settings reset",
      description: "TCGPlayer affiliate URL has been reset to default.",
    });
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-2xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your application preferences and configuration.
          </p>
        </div>

        {/* Scryfall API Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Scryfall API</CardTitle>
            <CardDescription>
              Configure the Scryfall API endpoint for card data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="scryfall-api-url">API Base URL</Label>
              <div className="flex gap-2">
                <Input
                  id="scryfall-api-url"
                  type="url"
                  value={scryfallApiUrl}
                  onChange={(e) => setScryfallApiUrl(e.target.value)}
                  placeholder="https://api.scryfall.com"
                  className="flex-1"
                />
                <Button
                  onClick={handleScryfallApiUrlSave}
                  disabled={scryfallApiUrl === config.scryfallApiUrl}
                >
                  Save
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                The base URL for the Scryfall API. Leave as default unless you're using a custom proxy or mirror.
              </p>
            </div>
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={scryfallApiUrl === 'https://api.scryfall.com'}
              >
                Reset to Default
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* TCGPlayer Affiliate Settings */}
        <Card>
          <CardHeader>
            <CardTitle>TCGPlayer Affiliate</CardTitle>
            <CardDescription>
              Configure your TCGPlayer affiliate link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tcgplayer-affiliate-url">Affiliate URL</Label>
              <div className="flex gap-2">
                <Input
                  id="tcgplayer-affiliate-url"
                  type="url"
                  value={tcgPlayerAffiliateUrl}
                  onChange={(e) => setTcgPlayerAffiliateUrl(e.target.value)}
                  placeholder="https://partner.tcgplayer.com/c/6367313/1830156/21018"
                  className="flex-1"
                />
                <Button
                  onClick={handleTcgPlayerAffiliateUrlSave}
                  disabled={tcgPlayerAffiliateUrl === config.tcgPlayerAffiliateUrl}
                >
                  Save
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Your TCGPlayer affiliate URL. This is used to generate purchase links.
              </p>
            </div>
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={handleTcgPlayerReset}
                disabled={tcgPlayerAffiliateUrl === 'https://partner.tcgplayer.com/c/6367313/1830156/21018'}
              >
                Reset to Default
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}